import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {CodonTranslationTable, GENETIC_CODE_USER_PREFERENECE_KEY, NucleotideType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {CustomField, Strand} from 'constants/MolbioConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {TRANSLATION_MAX_NOTES_LENGTH} from 'constants/SequenceConstants';
import {
  EditTranslationRegionGroupData,
  TranslationRegionModification,
  TranslationRegionModificationType,
} from 'molbio/dao/SequenceDAO';
import CustomFieldsForm from 'molbio/sequence-features/CustomFieldsForm';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import StrandDropdown from 'molbio/sequence-features/StrandDropdown';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import {
  countAminoAcids,
  DEFAULT_TRANSLATION_COLOR,
  translateRegions,
  TranslationRegionGroupData,
  TRANSLATION_EDIT_FIELDS,
  validateTranslationForm,
  confirmTranslationLinkInvalidationRisk,
} from 'molbio/translations/TranslationUtil';
import notifications from 'notifications';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {useFeaturePanelStateBySequence} from 'toolbar/feature-panel/reduceFeaturePanelUI';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import ToggleGroup from 'ui-library/ToggleGroup';
import {isEnterKey} from 'util/Keyboard';

import {CreateNewTranslationData} from './TranslationPanelUtil';
import TranslationPositionInputGroup from './TranslationPositionInputGroup';
import TranslationsDropdown from './TranslationsDropdown';

enum CreateRegionTo {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
}

export interface TranslationRegion {
  start: number | null;
  end: number | null;
  id?: number;
  selected?: boolean;
}

interface TranslationFormData {
  name: string;
  strand: Strand;
  notes: string;
  customFields: Array<CustomField>;
  codonTranslationTable: CodonTranslationTable;
  coerceStartCodonToMethionine: boolean;
  color: string;
  addingNew: boolean;
}

function getFormDataFromTranslation(
  group: TranslationRegionGroupData | null,
  preferredGeneticCode: CodonTranslationTable
): TranslationFormData {
  return {
    addingNew: true,
    name: group?.name ?? '',
    strand: group?.strand ?? 1,
    notes: group?.notes ?? '',
    customFields: group?.customFields ?? [],
    codonTranslationTable: group?.codonTranslationTable ?? preferredGeneticCode,
    color: group?.color ?? DEFAULT_TRANSLATION_COLOR,
    coerceStartCodonToMethionine: group?.coerceStartCodonToMethionine ?? false,
  };
}

function getAllUniqueCustomFieldKeys(allTranslationData: Array<TranslationRegionGroupData>): Array<string> {
  const uniqueKeys = new Set(
    allTranslationData.flatMap(({customFields}) => customFields.map(({name}) => name))
  );
  return [...uniqueKeys];
}

export interface CreateEditTranslationFormProps {
  // Data for all existing translations on the sequence.
  allTranslationData: Array<TranslationRegionGroupData>;
  // Data for the translation currently being edited. Only required if the form is used for editing.
  editedTranslationData: TranslationRegionGroupData | null;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  sequenceBases: string;
  nucleotideType: NucleotideType;
  isSingleStranded: boolean;
  isCircularSequence: boolean;
  onClickCancel: () => void;
  deleteTranslationRegionGroup: (groupCid: string) => Promise<void>;
  editTranslationRegionGroup: (groupCid: string, data: EditTranslationRegionGroupData) => void;
  createNewTranslationRegionGroup: (data: CreateNewTranslationData) => void;
  isReadOnly: boolean;
  tagSchema?: TagSchema;
}

/**
 * The form used in the translations tab of the feature panel in the molbio toolbar
 * to edit existing translations, create new translations, or add a translation region
 * to an existing translation.
 */
function CreateEditTranslationForm({
  allTranslationData,
  editedTranslationData,
  sequenceId,
  sequenceStartIndex,
  sequenceLength,
  sequenceBases,
  nucleotideType,
  isSingleStranded,
  isCircularSequence,
  onClickCancel,
  deleteTranslationRegionGroup,
  editTranslationRegionGroup,
  createNewTranslationRegionGroup,
  isReadOnly,
  tagSchema,
}: CreateEditTranslationFormProps): JSX.Element {
  const [geneticCodeUserPreference, setGeneticCodeUserPreference] = usePreference<CodonTranslationTable>(
    GENETIC_CODE_USER_PREFERENECE_KEY
  );
  const [translationFormData, setTranslationFormData] = useState<TranslationFormData>(
    getFormDataFromTranslation(editedTranslationData, geneticCodeUserPreference)
  );
  const [existingTranslationRegionGroup, setExistingTranslationRegionGroup] =
    useState<TranslationRegionGroupData | null>(editedTranslationData);
  const [regionsToShow, setRegionsToShow] = useState<Array<TranslationRegion>>([]);
  const [newRegionStart, setNewRegionStart] = useState<number | null>(null);
  const [newRegionEnd, setNewRegionEnd] = useState<number | null>(null);
  const {translationPanelState, selectionStart, selectionEnd} = useFeaturePanelStateBySequence(sequenceId);
  // If the form is to create new or add to existing, the form cannot be read only
  const {isCreateActive, initialStrand} = translationPanelState;
  const isAddingToExisting = isCreateActive && !translationFormData.addingNew;
  const isCreatingNew = isCreateActive && translationFormData.addingNew;
  const disableSave =
    (isAddingToExisting && !existingTranslationRegionGroup) ||
    (isCreateActive && (newRegionStart == null || newRegionEnd == null)) ||
    isReadOnly;

  useEffect(() => {
    if (isCreateActive) {
      setNewRegionStart(selectionStart);
      setNewRegionEnd(selectionEnd);
    }
  }, [isCreateActive, selectionStart, selectionEnd, setNewRegionStart, setNewRegionEnd]);

  useEffect(() => {
    const existingRegions =
      existingTranslationRegionGroup?.translations.map((t) => ({
        start: t.start,
        end: t.end,
        id: t.id,
      })) ?? [];
    setRegionsToShow(
      !isCreateActive ? existingRegions : [...existingRegions, {start: newRegionStart, end: newRegionEnd}]
    );
  }, [existingTranslationRegionGroup, newRegionStart, newRegionEnd, isCreateActive]);

  useEffect(() => {
    if (!isCreateActive) {
      setExistingTranslationRegionGroup(editedTranslationData);
    }
  }, [editedTranslationData, isCreateActive]);

  useEffect(() => {
    if (isCreateActive) {
      setTranslationFormData((currentData) => ({...currentData, strand: initialStrand ?? 1}));
      setExistingTranslationRegionGroup(null);
    }
  }, [isCreateActive, initialStrand]);

  useEffect(() => {
    if (isCreateActive) {
      setExistingTranslationRegionGroup(null);
    }
  }, [isCreateActive, translationFormData.strand]);

  function updateFormData(data: Partial<TranslationFormData>): void {
    setTranslationFormData({...translationFormData, ...data});
  }

  function onChangeExistingTranslation(group: TranslationRegionGroupData): void {
    setExistingTranslationRegionGroup(group);
    setTranslationFormData({
      ...getFormDataFromTranslation(group, geneticCodeUserPreference),
      addingNew: false,
    });
  }

  // Gets the start and end range of all regions in the currently edited translation group
  // including new regions being added
  // This assumes that existing regions are already listed in position order
  function translationGroupRange(): {start: number | null; end: number | null} {
    const completeRegions = regionsToShow.filter(({start, end}) => start != null && end != null);
    if (completeRegions.length === 0) {
      return {start: newRegionStart, end: newRegionEnd};
    } else {
      return {
        start: completeRegions[0].start,
        end: completeRegions[completeRegions.length - 1].end,
      };
    }
  }

  // Gets the region modification spec for the given translation ID according to the current form state
  // For a non deleted region, add an update spec if the strand has changed
  // Strand is a property on Translations, not Translation Groups, so when changing the strand,
  // each region of the entire translation group must be updated.
  function getModificationSpec(id: number): TranslationRegionModification | null {
    const regionIdsToUpdate = regionsToShow.map(({id}) => id);
    if (!regionIdsToUpdate.includes(id)) {
      return {
        modificationType: TranslationRegionModificationType.DELETE,
        id,
      };
    } else if (translationFormData.strand !== existingTranslationRegionGroup!.strand) {
      return {
        modificationType: TranslationRegionModificationType.UPDATE,
        id,
        strand: translationFormData.strand,
      };
    } else {
      return null;
    }
  }

  function trackOnClickSave(action: string, editedFields?: Array<string>): void {
    let translationIdMetadata: {[key: string]: number | null} = {
      translation_group_id: null,
      translation_id: null,
    };
    if (action !== 'create_new') {
      translationIdMetadata = {
        translation_group_id: existingTranslationRegionGroup!.hasGroup
          ? parseInt(existingTranslationRegionGroup!.cid, 10)
          : null,
        translation_id: existingTranslationRegionGroup!.hasGroup
          ? null
          : existingTranslationRegionGroup?.translations[0].id!,
      };
    }

    analytics.track('translation', 'on_click_save', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: sequenceId,
      action,
      edited_fields: editedFields ?? [],
      ...translationIdMetadata,
    });
  }

  async function onClickSave(): Promise<void> {
    const groupProperties = {
      name: translationFormData.name,
      notes: translationFormData.notes,
      customFields: translationFormData.customFields,
      codonTranslationTable: translationFormData.codonTranslationTable,
      color: translationFormData.color,
      coerceStartCodonToMethionine: translationFormData.coerceStartCodonToMethionine,
    };
    const adjustedNewRegionEnd = newRegionEnd === sequenceLength ? 0 : newRegionEnd;
    const regionProperties = {
      start: newRegionStart!,
      end: adjustedNewRegionEnd!,
      strand: translationFormData.strand,
    };
    // If a translation is not being fully deleted, validate the changes before saving
    if (regionsToShow.length > 0) {
      const errorMessage = validateTranslationForm(
        {
          name: translationFormData.name,
          translationRegionGroup: existingTranslationRegionGroup,
          isCreateActive,
          start: newRegionStart,
          end: adjustedNewRegionEnd,
          customFields: translationFormData.customFields,
        },
        sequenceStartIndex,
        sequenceLength,
        isCircularSequence
      );
      if (errorMessage != null) {
        notifications.error(errorMessage);
        return;
      }
    }

    if (isCreatingNew) {
      createNewTranslationRegionGroup({...groupProperties, ...regionProperties});
      trackOnClickSave('create_new');
      setGeneticCodeUserPreference(groupProperties.codonTranslationTable);
    } else if (isAddingToExisting) {
      editTranslationRegionGroup(existingTranslationRegionGroup!.cid, {
        regions: [{modificationType: TranslationRegionModificationType.CREATE, ...regionProperties}],
      });
      trackOnClickSave('add_to_existing');
    } else if (regionsToShow.length === 0) {
      // If all regions were removed from the translation, directly delete it
      await deleteTranslationRegionGroup(existingTranslationRegionGroup!.cid);
      trackOnClickSave('delete');
    } else {
      const editedFields = TRANSLATION_EDIT_FIELDS.filter(
        (field) => translationFormData[field] !== existingTranslationRegionGroup![field]
      );
      if (
        !(await confirmTranslationLinkInvalidationRisk(
          tagSchema,
          editedFields.includes('codonTranslationTable'),
          editedFields.includes('coerceStartCodonToMethionine') &&
            Boolean(editedTranslationData?.coerceStartCodonToMethionine),
          false,
          1
        ))
      ) {
        return;
      }
      editTranslationRegionGroup(existingTranslationRegionGroup!.cid, {
        regions: existingTranslationRegionGroup!.translations
          .map(({id}) => getModificationSpec(id))
          .filter((spec) => spec != null),
        properties: groupProperties,
      });
      trackOnClickSave('edit', editedFields);
      if (editedFields.includes('codonTranslationTable')) {
        setGeneticCodeUserPreference(groupProperties.codonTranslationTable);
      }
    }
  }

  return (
    <div
      className={css(styles.formContainer)}
      onKeyDown={(e) => {
        if (!disableSave && isEnterKey(e)) {
          onClickSave();
        }
      }}
      {...getTestProps({component: 'CreateEditTranslationForm', element: 'root'})}
    >
      {isCreateActive && (
        <FormGroup className={css(styles.createNewToggle)}>
          <FieldLabel className={css(styles.newTranslationTitle)} label="Create translation" />
          <ToggleGroup
            options={[
              {
                id: CreateRegionTo.NEW,
                buttonProps: {text: 'Add new'},
              },
              {
                id: CreateRegionTo.EXISTING,
                buttonProps: {text: 'Add to existing'},
              },
            ]}
            selection={translationFormData.addingNew ? CreateRegionTo.NEW : CreateRegionTo.EXISTING}
            onChangeSelection={(value) => {
              updateFormData({addingNew: value === CreateRegionTo.NEW});
              if (value === CreateRegionTo.NEW) {
                setExistingTranslationRegionGroup(null);
              }
            }}
            size={ButtonSize.MEDIUM}
            isFullWidth={true}
            disabled={allTranslationData.length === 0}
          />
        </FormGroup>
      )}

      {isAddingToExisting && (
        <>
          {existingTranslationRegionGroup == null && (
            <TranslationPositionInputGroup
              label="Position"
              modelRange={translationGroupRange()}
              setStart={setNewRegionStart}
              setEnd={setNewRegionEnd}
              disabled={false}
              sequenceStartIndex={sequenceStartIndex}
              sequenceLength={sequenceLength}
            />
          )}
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Strand" />
            <StrandDropdown
              className={css(sharedFormStyles.fieldInput)}
              strand={translationFormData.strand}
              onSelectStrand={(value: Strand) => {
                updateFormData({strand: value});
              }}
              disabled={isSingleStranded}
              allowNone={false}
            />
          </FormGroup>
          <TranslationsDropdown
            selectedTranslation={existingTranslationRegionGroup}
            onChangeSelectedTranslation={onChangeExistingTranslation}
            allTranslationData={allTranslationData.filter(({editable}) => editable)}
            sequenceStartIndex={sequenceStartIndex}
            sequenceLength={sequenceLength}
            selectedStrand={translationFormData.strand}
            region={{start: newRegionStart, end: newRegionEnd}}
          />
        </>
      )}

      {!isAddingToExisting && (
        <FormGroup className={css(sharedFormStyles.fieldRow)}>
          <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Name" />
          <TextInput
            className={css(sharedFormStyles.fieldInput)}
            placeholder="Translation name"
            value={translationFormData.name}
            onChange={(e) => {
              updateFormData({name: e.target.value});
            }}
            disabled={isAddingToExisting || isReadOnly}
            dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'nameInput'}}
          />
        </FormGroup>
      )}

      {!(isAddingToExisting && existingTranslationRegionGroup == null) && (
        <>
          <TranslationPositionInputGroup
            label="Position"
            modelRange={translationGroupRange()}
            setStart={setNewRegionStart}
            setEnd={setNewRegionEnd}
            disabled={!isCreatingNew}
            sequenceStartIndex={sequenceStartIndex}
            sequenceLength={sequenceLength}
          />
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(sharedFormStyles.inputLabel)} label="# of AA's" />
            <TextInput
              className={css(sharedFormStyles.fieldInput)}
              value={countAminoAcids(
                translateRegions(
                  regionsToShow,
                  sequenceBases,
                  translationFormData.strand,
                  nucleotideType,
                  translationFormData.codonTranslationTable,
                  translationFormData.coerceStartCodonToMethionine
                )
              )}
              disabled={true}
            />
          </FormGroup>
        </>
      )}

      {!(isAddingToExisting && existingTranslationRegionGroup == null) && (
        <>
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Genetic code" />
            <GeneticCodeDropdown
              className={css(sharedFormStyles.fieldInput)}
              geneticCode={translationFormData.codonTranslationTable}
              onSelectGeneticCode={(geneticCode: CodonTranslationTable) => {
                updateFormData({codonTranslationTable: geneticCode});
                analytics.track('translation', 'changed_genetic_code', {
                  product: ANALYTICS_MODULE.MOLBIO,
                  sequence_id: sequenceId,
                });
              }}
              disabled={isAddingToExisting || isReadOnly}
            />
          </FormGroup>
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(sharedFormStyles.inputLabel)} label="First codon" />
            <CheckboxInput
              className={css(sharedFormStyles.fieldInput)}
              onChange={(e) => {
                updateFormData({coerceStartCodonToMethionine: e.target.checked});
              }}
              checked={translationFormData.coerceStartCodonToMethionine}
              id="methionine-checkbox"
              label="Translate as methionine"
              disabled={isAddingToExisting || isReadOnly}
              dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'coerceStartCodonCheckbox'}}
            />
          </FormGroup>
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Color" />
            <PopoverColorPicker
              className={css(sharedFormStyles.fieldInput)}
              selectedColor={translationFormData.color}
              onSelectColor={(color) => {
                updateFormData({color});
              }}
              asDropdown={true}
              disabled={isReadOnly || isAddingToExisting}
            />
          </FormGroup>
        </>
      )}

      {!isAddingToExisting && (
        <>
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(sharedFormStyles.inputLabel)} label="Strand" />
            <StrandDropdown
              className={css(sharedFormStyles.fieldInput)}
              strand={translationFormData.strand}
              onSelectStrand={(value: Strand) => {
                updateFormData({strand: value});
              }}
              disabled={isSingleStranded || isReadOnly}
              allowNone={false}
            />
          </FormGroup>
          <FormGroup className={css(sharedFormStyles.fieldRow)}>
            <FieldLabel className={css(styles.notesLabel)} label="Notes" />
            <textarea
              className={css(sharedFormStyles.fieldInput)}
              placeholder="Notes"
              maxLength={TRANSLATION_MAX_NOTES_LENGTH}
              value={translationFormData.notes}
              onChange={(e) => {
                updateFormData({notes: e.target.value});
              }}
              rows={translationFormData.notes ? 3 : 1}
              disabled={isReadOnly}
            />
          </FormGroup>
        </>
      )}

      {!(isAddingToExisting && existingTranslationRegionGroup == null) && (
        <CustomFieldsForm
          className={css(styles.customFields)}
          customFields={translationFormData.customFields}
          setCustomFields={(newFields) => {
            updateFormData({customFields: newFields});
          }}
          allCustomFieldKeys={getAllUniqueCustomFieldKeys(allTranslationData)}
          emptyFieldsMessage="This translation has no custom fields"
          isReadOnly={isAddingToExisting || isReadOnly}
        />
      )}

      {!isCreatingNew && existingTranslationRegionGroup != null && (
        <>
          <FieldLabel className={css(styles.regionsSectionLabel)} label="Regions" />
          {regionsToShow.map((region, idx) => (
            <TranslationPositionInputGroup
              key={idx}
              regionLabelClassName={css(styles.regionsLabel)}
              label={`Region ${idx + 1}`}
              modelRange={region}
              setStart={setNewRegionStart}
              setEnd={setNewRegionEnd}
              disabled={!isCreateActive || idx < regionsToShow.length - 1}
              sequenceStartIndex={sequenceStartIndex}
              sequenceLength={sequenceLength}
              onDeleteRegion={
                isCreateActive || isReadOnly
                  ? null
                  : (idToRemove) => {
                      setRegionsToShow(regionsToShow.filter(({id}) => id !== idToRemove));
                    }
              }
            />
          ))}
        </>
      )}

      <div className={css(styles.buttonRow)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={onClickCancel}
          text="Cancel"
        />
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={onClickSave}
          text="Save"
          disabled={disableSave}
          dataTestAttrs={{component: 'CreateEditTranslationForm', element: 'saveButton'}}
        />
      </div>
    </div>
  );
}

export const sharedFormStyles = StyleSheet.create({
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 12px',
  },
  inputLabel: {
    margin: 'auto 0',
  },
  fieldInput: {
    width: '172px',
  },
});

const styles = StyleSheet.create({
  createNewToggle: {
    padding: '0 12px',
  },
  newTranslationTitle: {
    marginBottom: '16px',
  },
  formContainer: {
    padding: '12px 0',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${GRAY_LIGHT}`,
    padding: '16px 10px 0',
  },
  regionsSectionLabel: {
    color: GRAY_DARKER,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    width: 'calc(100% - 24px)',
    margin: '0 12px 12px',
    paddingBottom: '8px',
  },
  regionsLabel: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    margin: 'auto 0',
  },
  customFields: {
    margin: '16px 12px',
  },
  notesLabel: {
    marginTop: 6,
  },
});

export default CreateEditTranslationForm;
